import { KeyOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Drawer, Flex, Form, Input, Select, Switch, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListPage } from '../../components/ListPage';
import { ApiError } from '../../lib/api-client';
import { useSession } from '../auth/session';
import {
  createUser,
  fetchBranchOptions,
  fetchRoles,
  fetchUsers,
  setUserPassword,
  updateUser,
  type CreateUserInput,
  type UserRow,
} from './api';
import { StaffImportDrawer } from './StaffImportDrawer';

const { Text } = Typography;

interface EditUserValues {
  name: string;
  email?: string;
  isActive: boolean;
  roleUids: string[];
  branchUids: string[];
}

/**
 * Who can sign in, and what they can reach (CLAUDE.md §11).
 *
 * The API refuses to let anyone deactivate or re-role their own account, since
 * there is no recovery path short of a database edit. This screen makes that
 * visible rather than letting someone discover it by being refused: the
 * controls are disabled on your own row, with the reason shown.
 */
export function UsersPage(): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const me = useSession((s) => s.user);

  const [editing, setEditing] = useState<UserRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<UserRow | null>(null);
  const [importing, setImporting] = useState(false);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const users = useQuery({ queryKey: ['iam', 'users'], queryFn: fetchUsers });
  const roles = useQuery({ queryKey: ['iam', 'roles'], queryFn: fetchRoles });
  const branches = useQuery({ queryKey: ['iam', 'branches'], queryFn: fetchBranchOptions });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['iam'] });
  };

  // Every mutation reports the API's own message rather than inventing one.
  // The self-lockout refusals explain exactly why, and rewording them here
  // would lose that.
  const fail = (e: unknown): void => {
    message.error(e instanceof ApiError ? e.message : t('iam.genericError'));
  };

  const create = useMutation({
    mutationFn: createUser,
    onSuccess: (user) => {
      message.success(t('iam.userCreated', { name: user.name, username: user.username }));
      setCreating(false);
      createForm.resetFields();
      refresh();
    },
    onError: fail,
  });

  const update = useMutation({
    mutationFn: ({ uid, input }: { uid: string; input: Parameters<typeof updateUser>[1] }) =>
      updateUser(uid, input),
    onSuccess: () => {
      message.success(t('iam.userSaved'));
      setEditing(null);
      refresh();
    },
    onError: fail,
  });

  const resetPassword = useMutation({
    mutationFn: ({ uid, password }: { uid: string; password: string }) =>
      setUserPassword(uid, password),
    onSuccess: () => {
      message.success(t('iam.passwordSet'));
      setResetting(null);
      passwordForm.resetFields();
      refresh();
    },
    onError: fail,
  });

  const roleOptions = (roles.data ?? []).map((r) => ({ value: r.uid, label: r.name }));
  const branchOptions = (branches.data ?? []).map((b) => ({ value: b.uid, label: b.name }));

  const isMe = (row: UserRow): boolean => row.uid === me?.uid;

  return (
    <>
      <ListPage<UserRow>
        title={t('iam.users')}
        loading={users.isLoading}
        emptyDescription={t('iam.noUsers')}
        rows={users.data ?? []}
        rowKey={(row) => row.uid}
        actions={
          <Flex gap={8}>
            <Button icon={<UploadOutlined />} onClick={() => { setImporting(true); }}>
              {t('iam.import.open')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setCreating(true); }}>
              {t('iam.addUser')}
            </Button>
          </Flex>
        }
        onRowClick={(row) => {
          setEditing(row);
          editForm.setFieldsValue({
            name: row.name,
            email: row.email ?? undefined,
            isActive: row.isActive,
            roleUids: row.roles.map((r) => r.uid),
            branchUids: row.branches.map((b) => b.uid),
          });
        }}
        columns={[
          {
            title: t('iam.username'),
            dataIndex: 'username',
            width: 160,
            render: (username: string, row: UserRow) => (
              <Flex gap={6} align="center">
                <span className="tabular">{username}</span>
                {isMe(row) && <Tag color="processing">{t('iam.you')}</Tag>}
              </Flex>
            ),
          },
          { title: t('iam.name'), dataIndex: 'name', width: 220 },
          {
            title: t('iam.roles'),
            width: 280,
            render: (_: unknown, row: UserRow) => (
              <Flex gap={4} wrap>
                {row.roles.length === 0 ? (
                  // A user with no role can sign in and reach nothing. Worth
                  // saying plainly rather than showing an empty cell.
                  <Tag color="warning">{t('iam.noRoles')}</Tag>
                ) : (
                  row.roles.map((r) => <Tag key={r.uid}>{r.name}</Tag>)
                )}
              </Flex>
            ),
          },
          {
            title: t('iam.branches'),
            width: 200,
            render: (_: unknown, row: UserRow) => (
              <Text type="secondary">{row.branches.map((b) => b.name).join(', ') || '—'}</Text>
            ),
          },
          {
            title: t('iam.status'),
            width: 140,
            render: (_: unknown, row: UserRow) => (
              <Flex gap={4}>
                <Tag color={row.isActive ? 'success' : 'default'}>
                  {row.isActive ? t('iam.active') : t('iam.inactive')}
                </Tag>
                {/* Locked is separate from inactive: one is a security event,
                    the other a decision. Resetting the password clears it. */}
                {row.isLocked && <Tag color="error">{t('iam.locked')}</Tag>}
              </Flex>
            ),
          },
          {
            title: '',
            width: 150,
            render: (_: unknown, row: UserRow) => (
              <Button
                size="small"
                icon={<KeyOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setResetting(row);
                }}
              >
                {t('iam.setPassword')}
              </Button>
            ),
          },
        ]}
        renderMobileRow={(row) => (
          <Flex vertical gap={4}>
            <Flex justify="space-between" align="center">
              <Text strong className="tabular">
                {row.username}
              </Text>
              <Tag color={row.isActive ? 'success' : 'default'}>
                {row.isActive ? t('iam.active') : t('iam.inactive')}
              </Tag>
            </Flex>
            <Text>{row.name}</Text>
            <Flex gap={4} wrap>
              {row.roles.map((r) => (
                <Tag key={r.uid}>{r.name}</Tag>
              ))}
            </Flex>
          </Flex>
        )}
      />

      <StaffImportDrawer
        open={importing}
        onClose={() => { setImporting(false); }}
        onCreated={refresh}
      />

      {/* ── create ─────────────────────────────────────────────── */}
      <Drawer
        className="nks-action-drawer"
        width={460}
        open={creating}
        title={t('iam.addUser')}
        onClose={() => { setCreating(false); }}
        extra={
          <Button
            type="primary"
            loading={create.isPending}
            onClick={() => {
              void createForm.validateFields().then((values: CreateUserInput) => {
                create.mutate(values);
              });
            }}
          >
            {t('iam.createUser')}
          </Button>
        }
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="username"
            label={t('iam.username')}
            rules={[{ required: true }]}
            extra={t('iam.usernameHint')}
          >
            <Input size="large" autoFocus autoComplete="off" />
          </Form.Item>
          <Form.Item name="name" label={t('iam.name')} rules={[{ required: true }]}>
            <Input size="large" />
          </Form.Item>
          <Form.Item name="employeeNo" label={t('iam.employeeNo')} rules={[{ required: true }]}>
            <Input size="large" />
          </Form.Item>
          <Form.Item name="email" label={t('iam.email')} extra={t('iam.emailHint')}>
            <Input size="large" inputMode="email" />
          </Form.Item>
          <Form.Item
            name="password"
            label={t('iam.password')}
            rules={[{ required: true, min: 8, message: t('iam.passwordRule') }]}
            extra={`${t('iam.passwordHint')} ${t('iam.adminPasswordHint')}`}
          >
            {/* Input.Password carries the show/hide eye. */}
            <Input.Password size="large" autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="roleUids" label={t('iam.roles')} initialValue={[]}>
            <Select mode="multiple" size="large" options={roleOptions} />
          </Form.Item>
          <Form.Item
            name="branchUids"
            label={t('iam.branches')}
            rules={[{ required: true, message: t('iam.branchRequired') }]}
            extra={t('iam.branchHint')}
          >
            <Select mode="multiple" size="large" options={branchOptions} />
          </Form.Item>
        </Form>
      </Drawer>

      {/* ── edit ───────────────────────────────────────────────── */}
      <Drawer
        className="nks-action-drawer"
        width={460}
        open={editing !== null}
        title={editing?.name ?? ''}
        onClose={() => { setEditing(null); }}
        extra={
          <Button
            type="primary"
            loading={update.isPending}
            onClick={() => {
              void editForm.validateFields().then((values: EditUserValues) => {
                if (editing === null) return;
                // Never send roles or isActive for your own account — the API
                // refuses, and there is no reason to make the round trip.
                const input = isMe(editing)
                  ? { name: values.name, email: values.email, branchUids: values.branchUids }
                  : values;
                update.mutate({ uid: editing.uid, input });
              });
            }}
          >
            {t('iam.save')}
          </Button>
        }
      >
        {editing !== null && (
          <Form form={editForm} layout="vertical">
            <Form.Item label={t('iam.username')}>
              {/* Not editable. It is the sign-in identity and changing it would
                  lock somebody out of a session they are holding. */}
              <Input size="large" value={editing.username} disabled />
            </Form.Item>
            <Form.Item name="name" label={t('iam.name')} rules={[{ required: true }]}>
              <Input size="large" />
            </Form.Item>
            <Form.Item name="email" label={t('iam.email')}>
              <Input size="large" inputMode="email" />
            </Form.Item>
            <Form.Item
              name="roleUids"
              label={t('iam.roles')}
              extra={isMe(editing) ? t('iam.cannotChangeOwnRoles') : undefined}
            >
              <Select mode="multiple" size="large" options={roleOptions} disabled={isMe(editing)} />
            </Form.Item>
            <Form.Item name="branchUids" label={t('iam.branches')} rules={[{ required: true }]}>
              <Select mode="multiple" size="large" options={branchOptions} />
            </Form.Item>
            <Form.Item
              name="isActive"
              label={t('iam.active')}
              valuePropName="checked"
              extra={isMe(editing) ? t('iam.cannotDeactivateSelf') : undefined}
            >
              <Switch disabled={isMe(editing)} />
            </Form.Item>
          </Form>
        )}
      </Drawer>

      {/* ── reset password ─────────────────────────────────────── */}
      <Drawer
        className="nks-action-drawer"
        width={420}
        open={resetting !== null}
        title={t('iam.setPasswordFor', { name: resetting?.name ?? '' })}
        onClose={() => { setResetting(null); passwordForm.resetFields(); }}
        extra={
          <Button
            type="primary"
            loading={resetPassword.isPending}
            onClick={() => {
              void passwordForm.validateFields().then((values: { password: string }) => {
                if (resetting === null) return;
                resetPassword.mutate({ uid: resetting.uid, password: values.password });
              });
            }}
          >
            {t('iam.setPassword')}
          </Button>
        }
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            name="password"
            label={t('iam.newPassword')}
            rules={[{ required: true, min: 8, message: t('iam.passwordRule') }]}
            extra={`${t('iam.passwordHint')} ${t('iam.adminPasswordHint')}`}
          >
            <Input.Password size="large" autoFocus autoComplete="new-password" />
          </Form.Item>
          {resetting?.isLocked === true && (
            <Text type="secondary">{t('iam.willUnlock')}</Text>
          )}
        </Form>
      </Drawer>
    </>
  );
}
